import {TFunction} from 'i18next';
import {forEach, includes, transform} from 'lodash';

import {CustomField, Group, UserInfo} from 'interfaces';
import {TRANSLATION_KEYS} from 'locale';
import {getGroupName, getSubgroupName} from 'services';
import {AssignmentData, ClientConnect} from 'types';
import {userInfoUtils} from 'utils';
import {buildTranslationKey} from 'utils/transform';

const T_KEYS = TRANSLATION_KEYS.USER.ACCOUNT.CLIENTS_CONNECTIONS;
const COMMON_T_KEYS = TRANSLATION_KEYS.COMMON;

const getHeaderNameByField = (field: CustomField, t: TFunction): string => {
    const key = buildTranslationKey(field.name);
    return T_KEYS.FIELDS[key] ? t(T_KEYS.FIELDS[key]) : field.title;
};

// is it possible to get rid of reverse iteration here???
const getFieldByHeaderName = (header: string, userInfoSchemaData: AssignmentData<CustomField[]>[], t: TFunction): CustomField => {
    let fieldResult = null;
    forEach(userInfoSchemaData, (schema) => {
        const userInfoFields = userInfoUtils.getUserInfoFields(schema.data);
        forEach(userInfoFields, (field) => {
            const fieldHeader = getHeaderNameByField(field, t);
            if (header === fieldHeader) {
                fieldResult = field;
                return false;
            }
        });

        if (!fieldResult) {
            const controlOnlyFields = userInfoUtils.getControlOnlyFields(schema.data);
            forEach(controlOnlyFields, (field) => {
                const fieldHeader = getHeaderNameByField(field, t);
                if (header === fieldHeader) {
                    fieldResult = field;
                    return false;
                }
            });
        }

    });

    return fieldResult;
};

const getFullUserInfoFieldLabels = (userInfoSchemaData: AssignmentData<CustomField[]>[], t: TFunction) => {
    const userInfoFieldsResult: string[] = [];
    const controlOnlyFieldsResult: string[] = [];

    forEach(userInfoSchemaData, (schema) => {
        const userInfoFields = userInfoUtils.getUserInfoFields(schema.data);
        forEach(userInfoFields, (field) => {
            const name = getHeaderNameByField(field, t);
            if (!includes(userInfoFieldsResult, name)) {
                userInfoFieldsResult.push(name);
            }
        });

        const controlOnlyFields = userInfoUtils.getControlOnlyFields(schema.data);
        forEach(controlOnlyFields, (field) => {
            const name = getHeaderNameByField(field, t);
            if (!includes(controlOnlyFieldsResult, name)) {
                controlOnlyFieldsResult.push(name);
            }
        });
    });

    return {
        userInfoFields: userInfoFieldsResult,
        controlOnlyFields: controlOnlyFieldsResult,
    };
};

const extractUserInfo = (row: Record<string, string>, customFields: CustomField[], t: TFunction): UserInfo => {
    const userInfo = {
        controlOnlyInfo: {}
    };

    forEach(customFields, (field) => {
        const name = getHeaderNameByField(field, t);
        if (row[name]) {
            userInfo[field.name] = row[name];
        }

        forEach(field?.children, (controlOnlyField) => {
            const controlOnlyFieldHeader = getHeaderNameByField(controlOnlyField, t);
            if (row[controlOnlyFieldHeader]) {
                userInfo.controlOnlyInfo[field.name] = row[controlOnlyFieldHeader];
            }
        });
    });

    return userInfo as UserInfo;
};

const getHeader = (
    t: TFunction,
    {isTemplate, userInfoFields, controlOnlyFields}: {userInfoFields: string[], controlOnlyFields: string[], isTemplate: boolean}
) => {

    const commonHeaderNames = [
        t(T_KEYS.FIELDS.GROUP),
        t(T_KEYS.FIELDS.SUBGROUP),
        t(COMMON_T_KEYS.SESSION.LABELS.AGENT),
        t(T_KEYS.FIELDS.COMMENTS)
    ];

    const contactHeaders = [
        t(T_KEYS.FIELDS.CONTACTS_PHONE),
        t(T_KEYS.FIELDS.CONTACTS_EMAIL)
    ];

    return [
        ...userInfoFields,
        ...contactHeaders,
        ...commonHeaderNames,
        ...(!isTemplate ? [t(T_KEYS.FIELDS.LINK)] : []),
        t(T_KEYS.FIELDS.ENABLED),
        ...controlOnlyFields
    ];
};

const exportConnect = (
    t: TFunction,
    connect: ClientConnect,
    {userInfoFields, controlOnlyFields}: {userInfoFields: string[], controlOnlyFields: string[]},
    groups: Group[],
    userInfoSchemaData: AssignmentData<CustomField[]>[]
) => {

    const userInfoCells = transform(userInfoFields, (acc, headerName) => {
        const field = getFieldByHeaderName(headerName, userInfoSchemaData, t);
        acc[headerName] = connect.userInfo[field?.name] || '';
    }, {});

    const contactCells = transform(connect.contacts,
        (acc, _value: string, contactProperty: keyof ClientConnect['contacts']) => {
            const headerName = contactProperty === 'phone' ? t(T_KEYS.FIELDS.CONTACTS_PHONE) : t(T_KEYS.FIELDS.CONTACTS_EMAIL);
            acc[headerName] = connect.contacts?.[contactProperty] || '';
        }, {});

    const controlOnlyCells = transform(controlOnlyFields, (acc, headerName) => {
        const field = getFieldByHeaderName(headerName, userInfoSchemaData, t);
        acc[headerName] = connect.userInfo.controlOnlyInfo?.[field?.name] || '';
    }, {});

    return {
        ...userInfoCells,
        ...contactCells,
        [t(T_KEYS.FIELDS.GROUP)]: getGroupName(connect.assignmentInfo.groupId, groups),
        [t(T_KEYS.FIELDS.SUBGROUP)]:
            getSubgroupName(
                connect.assignmentInfo.groupId,
                connect.assignmentInfo.subgroupId,
                groups
            ),
        [t(COMMON_T_KEYS.SESSION.LABELS.AGENT)]: connect.agent?.email,
        [t(T_KEYS.FIELDS.COMMENTS)]: connect.comments,
        [t(T_KEYS.FIELDS.LINK)]: connect.link,
        [t(T_KEYS.FIELDS.ENABLED)]: t(connect.disabled ? COMMON_T_KEYS.ALL.NO : COMMON_T_KEYS.ALL.YES),
        ...controlOnlyCells
    };
};

export const exportUtils = {
    getHeaderNameByField,
    exportConnect,
    getHeader,
    extractUserInfo,
    getFullUserInfoFieldLabels
};